import { convertCurrentTimeToPSTFormat } from '@/components/helper';
import { YMS_API_CONFIG } from '@/constants/endpoints';
import { taskcontants } from '@/constants/taskConstants';
import { store } from '@/store/store';
import {
  mapOICMoveTask,
  mapOICTrailer,
  type OICMoveTaskDetail,
  type OICTrailerDetail,
} from '@/utils/moveTaskMappers';
import { mapOICLocation, type OICLocationDetail } from '@/utils/oicMappers';

import { getAccessToken, oicFetch } from './ManhattanService';
import {
  CreateMoveTaskUpdateTaskDetails,
  type CreateMoveTaskResponse,
  type MoveLocationDetail,
  type MoveUpdateTaskDetailsResponse,
  type TaskDetail,
  type TrailerModel,
} from './types/movetasktypes';

// ─────────────────────────────────────────────────────────────────
// Types for Red Tag (Mark As Damaged) API
// ─────────────────────────────────────────────────────────────────

export interface UpdateRedTagTrailerParams {
  trailerName: string;
  trailerId: string;
  trailerCondition: number;
  trailerStatus: number;
  carrierScac: string;
  lockReasonCode: number;
}

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

function getOrgId(): string {
  return String(store.getState().user.plant?.org_id ?? '');
}

function getUserId(): string {
  const user = store.getState().user.user;
  // Use persistent user profile fields (not OTP flow fields which get cleared after login)
  return (user?.country_code ?? '') + (user?.mobile_no ?? '');
}

function getBaseUrl(): string {
  // DEV: Vite proxy handles CORS. PROD: Azure Function proxy avoids browser CORS.
  return import.meta.env.DEV ? '/middleware_new/' : '/api/oic-proxy/';
}

// ─────────────────────────────────────────────────────────────────
// Get Trailer List for Move Task
// ─────────────────────────────────────────────────────────────────

export async function GetTrailersListAsync(): Promise<TrailerModel[]> {
  const orgId = getOrgId();
  const userId = getUserId();
  const maBaseUrl = getBaseUrl();

  const endpointUrl = `${maBaseUrl}${YMS_API_CONFIG.MAGetTrailerListAPI}`;

  const token = await getAccessToken();

  const response = await oicFetch(endpointUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      payload: {
        TransmissionHeader: {
          RoutingCode: taskcontants.RoutingCode,
          TransmissionUser: 'nblyms',
          OriginatingSystem: taskcontants.OriginatingSystem,
          TransmissionDateTime: convertCurrentTimeToPSTFormat(),
          MessageType: 'getTrailerList',
          MessageId: taskcontants.MessageId,
        },
        Body: {
          Org_id: orgId,
          Filter: '',
          Size: '',
          Sort: 'CARRIER_SCAC ASC',
          User_id: userId,
        },
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`Trailer list API error: ${String(response.status)} ${response.statusText}`);
  }

  const raw = (await response.json()) as Record<string, unknown>;

  // Handle OIC wrapped response: payload.Body.Trailers or payload.Body.Trailer_details
  const payload = (raw['Payload'] ?? raw['payload']) as Record<string, unknown> | undefined;
  const body = payload?.['Body'] as Record<string, unknown> | undefined;

  // Try multiple possible paths for trailer array
  const rawTrailers = (body?.['Trailer_details'] ??
    body?.['Trailers'] ??
    raw['Trailer_details'] ??
    raw['Trailers']) as OICTrailerDetail[] | undefined;

  return rawTrailers?.map(mapOICTrailer) ?? [];
}

// ─────────────────────────────────────────────────────────────────
// Get Location List for Move Task Destination
// ─────────────────────────────────────────────────────────────────

export async function getMoveLocationList(locationtype: string): Promise<MoveLocationDetail[]> {
  const orgId = getOrgId();
  const userId = getUserId();
  const maBaseUrl = getBaseUrl();

  const endpointUrl = `${maBaseUrl}${YMS_API_CONFIG.MAGetLocationsListAPI}`;

  const token = await getAccessToken();

  const response = await oicFetch(endpointUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      payload: {
        TransmissionHeader: {
          TransmissionDateTime: convertCurrentTimeToPSTFormat(),
          OriginatingSystem: taskcontants.OriginatingSystem,
          TransmissionUser: 'nblyms',
          RoutingCode: taskcontants.RoutingCode,
          MessageType: taskcontants.MessageTypeGetLocationDetailList,
          MessageId: taskcontants.MessageId,
        },
        Body: {
          Org_id: orgId,
          Size: '',
          Sort: taskcontants.SortOrderLocationDetailList,
          User_id: userId,
          Location_type: locationtype,
        },
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`Location list API error: ${String(response.status)} ${response.statusText}`);
  }

  const raw = (await response.json()) as Record<string, unknown>;
  const locations = raw['Locations'] as OICLocationDetail[] | undefined;
  const mapped = locations?.map(mapOICLocation) ?? [];

  return mapped.filter((v) => v.LocStatus === taskcontants.Available);
}

// ─────────────────────────────────────────────────────────────────
// Get All Move Tasks List
// ─────────────────────────────────────────────────────────────────

export async function getAllMoveTasksList(): Promise<TaskDetail[]> {
  const orgId = getOrgId();
  const maBaseUrl = getBaseUrl();

  const endpointUrl = `${maBaseUrl}${YMS_API_CONFIG.MAGetTaskListAPI}`;

  const token = await getAccessToken();

  const response = await oicFetch(endpointUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      payload: {
        TransmissionHeader: {
          TransmissionDateTime: convertCurrentTimeToPSTFormat(),
          OriginatingSystem: taskcontants.OriginatingSystem,
          TransmissionUser: 'nblyms',
          RoutingCode: taskcontants.RoutingCode,
          MessageType: 'GetTasks',
          MessageId: taskcontants.MessageId,
        },
        Body: {
          Org_id: orgId,
          Response_record_limit: 1000,
          Task_type: null,
          Filter_string: '',
          Sort_order: 'STATUS DESC, LAST_UPDATED_DTTM DESC',
        },
      },
    }),
  });

  if (!response.ok) {
    return [];
  }

  const raw = (await response.json()) as Record<string, unknown>;

  // OIC response path: Payload.Body.Task.Task_details[]
  const payload = (raw['Payload'] ?? raw['payload']) as Record<string, unknown> | undefined;
  const body = payload?.['Body'] as Record<string, unknown> | undefined;
  const task = body?.['Task'] as Record<string, unknown> | undefined;
  const rawDetails = task?.['Task_details'] as OICMoveTaskDetail[] | undefined;

  const allTasks = rawDetails?.map(mapOICMoveTask) ?? [];

  // Filter to only move tasks with relevant statuses
  const moveTasks = allTasks.filter(
    (t) =>
      t.TaskType === taskcontants.MoveTaskType &&
      (t.Status === taskcontants.Assigned_status ||
        t.Status === taskcontants.InProgress_status ||
        t.Status === taskcontants.Released_status)
  );

  return moveTasks;
}

// ─────────────────────────────────────────────────────────────────
// Create Move Task (Submit)
// ─────────────────────────────────────────────────────────────────

export async function handleCompleteCallAsync(
  currentTask: CreateMoveTaskUpdateTaskDetails
): Promise<CreateMoveTaskResponse> {
  const orgId = getOrgId();
  const token = await getAccessToken();

  // Field names and structure match the exact Xamarin payload
  const oicTaskDetails = {
    Carrier_code: currentTask.CarrierCode,
    Alternate_carrier_code: currentTask.AlternateCarrierCode || 'false',
    Move_Destination_Type: currentTask.MoveDestinationType,
    Alternate_Destination_Name: currentTask.AlternateDestinationName.trim(),
    Trailer_name: currentTask.TrailerName,
    Move_Source_Name: currentTask.MoveSourceName,
    Alternate_Destination_Zone: currentTask.AlternateDestinationZone.trim(),
    Alternate_trailer_name: currentTask.AlternateTrailerName.trim(),
    Move_Destination_Zone: currentTask.MoveDestinationZone,
    Action_type: currentTask.ActionType,
    Move_Source_Zone: currentTask.MoveSourceZone,
    Exception_code: currentTask.ExceptionCode.trim(),
    Task_type: currentTask.TaskType,
    Move_Destination_Name: currentTask.MoveDestinationName,
    Org_id: currentTask.OrgId,
    Trailer_comments: currentTask.TrailerComments || null,
    Move_Source_Type: currentTask.MoveSourceType,
    User_id: currentTask.UserId,
    Task_Comments: currentTask.TaskComments || null,
    Alternate_trailer_valid: currentTask.AlternateTrailerValid ? 'Y' : '',
  };

  const createMoveTaskRequest = {
    payload: {
      TransmissionHeader: {
        TransmissionDateTime: convertCurrentTimeToPSTFormat(),
        OriginatingSystem: taskcontants.OriginatingSystem,
        TransmissionUser: 'nblyms',
        RoutingCode: taskcontants.RoutingCode,
        MessageType: 'updateTasks',
        MessageId: taskcontants.MessageId,
      },
      Body: {
        Org_id: orgId,
        Update_task_details: oicTaskDetails,
        Stop_on_first_action_error: taskcontants.StopOnFirstError,
      },
    },
    TraceData: {
      User: store.getState().user.user?._id ?? '',
      Plant: store.getState().user.plant?._id ?? '',
      SessionId: store.getState().user.sessionId ?? '',
    },
  };

  const maBaseUrl = getBaseUrl();
  const endpointUrl = `${maBaseUrl}${YMS_API_CONFIG.MACreateMoveTasksAPI}`;

  const response = await oicFetch(endpointUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(createMoveTaskRequest),
  });

  if (!response.ok) {
    throw new Error(`Move task API error: ${String(response.status)} ${response.statusText}`);
  }

  const raw = (await response.json()) as Record<string, unknown>;

  // API may return 'Payload' or 'payload' — normalize to PascalCase
  const payload = (raw['Payload'] ?? raw['payload']) as Record<string, unknown> | undefined;
  const body = payload?.['Body'] as Record<string, unknown> | undefined;

  const status = body?.['Status'];
  const statusMessage = body?.['Status_message'] ?? body?.['StatusMessage'];
  const statusCode = body?.['Status_code'] ?? body?.['StatusCode'];

  const data: CreateMoveTaskResponse = {
    Payload: {
      TransmissionHeader: (payload?.['TransmissionHeader'] ??
        {}) as CreateMoveTaskResponse['Payload']['TransmissionHeader'],
      Body: {
        Status: typeof status === 'string' ? status : '',
        StatusMessage: typeof statusMessage === 'string' ? statusMessage : '',
        StatusCode: typeof statusCode === 'string' ? statusCode : '',
        UpdateTaskDetails: (body?.['Update_task_details'] ??
          body?.['UpdateTaskDetails'] ??
          []) as MoveUpdateTaskDetailsResponse[],
      },
    },
  };

  return data;
}

// ─────────────────────────────────────────────────────────────────
// Update Red Tag Trailer (Mark As Damaged)
// Matches Xamarin UpdateTrailerRedTag API call
// ─────────────────────────────────────────────────────────────────

export async function updateRedTagTrailer(params: UpdateRedTagTrailerParams): Promise<void> {
  const orgId = getOrgId();
  const userId = getUserId();
  const maBaseUrl = getBaseUrl();

  const endpointUrl = `${maBaseUrl}${YMS_API_CONFIG.MAUpdateRedTagTrailerAPI}`;
  const token = await getAccessToken();

  const response = await oicFetch(endpointUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      trailer_name: params.trailerName,
      org: orgId,
      user_ID: userId,
      trailer_ID: params.trailerId,
      trailer_condition: params.trailerCondition,
      trailer_status: params.trailerStatus,
      carrier_scac: params.carrierScac,
      lock_reason_code: params.lockReasonCode,
      TraceData: {
        User: store.getState().user.user?._id ?? '',
        Plant: store.getState().user.plant?._id ?? '',
        SessionId: store.getState().user.sessionId ?? '',
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`Update Red Tag API error: ${String(response.status)} ${response.statusText}`);
  }
}
